import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ControlContainer, FormControl, FormGroupDirective, UntypedFormGroup } from '@angular/forms';
import { CodeConversionMstDto, InspectionTreatmentTab, ShishinInputTab, SuppntInfo } from 'sg-api';

/**
 * チェックリストインターフェース(内部使用の型）
 *
 * @interface CheckList
 */
interface CheckList {
  /** コントロール名 */
  control: string;
  /** 次の画面部品を有効にする比較値 */
  value: string;
  /** チェック有効フラグ true:チェック有効、false:チェックスキップ*/
  enable: boolean;
  /** チェック関数 */
  check: (prev: FormControl, value?: any) => boolean;
}

@Component({
  selector: 'app-sgke360d-inspection-treatment',
  templateUrl: './sgke360d-inspection-treatment.component.html',
  styleUrl: './sgke360d-inspection-treatment.component.scss',
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }]
})
export class Sgke360dInspectionTreatmentComponent implements OnInit, OnDestroy {
  /** 基本情報のレスポンス */
  @Input() suppntInfo: SuppntInfo = null;
  /** 事前確認タブ内のレスポンス */
  @Input() inspectionTreatmentTab: InspectionTreatmentTab = null;
  /** 指針入力タブ内のレスポンス */
  @Input() shishinInputTab: ShishinInputTab = null;
  /** コード変換マスタマップ */
  @Input() codeMap: Map<string, CodeConversionMstDto[]> = null;

  /** 作業種別コード */
  sagyoshubetsuCD: string;

  /** 親フォームグループ */
  public parentForm: UntypedFormGroup;
  /** タブ内フォームグループ */
  public adconfirmTabForm: UntypedFormGroup;

  /** メーターガス栓閉止チェックボックス */
  public get meterGassenShutoffflag(): boolean {
    return this.getControl('meterGassenShutoffflag')?.value == '1';
  }
  public set meterGassenShutoffflag(v: boolean) {
    this.getControl('meterGassenShutoffflag')?.setValue(v ? '1' : '0');
    this.onChangeCheck();
  }
  /** メーター休止措置チェックボックス */
  public get meterPauseSochiflag(): boolean {
    return this.getControl('meterPauseSochiflag')?.value == '1';
  }
  public set meterPauseSochiflag(v: boolean) {
    this.getControl('meterPauseSochiflag')?.setValue(v ? '1' : '0');
    this.onChangeCheck();
  }

  /**
   * コンストラクタ
   * @param {FormGroupDirective} parent
   * @param {CodeConversionMstService} codeConvert
   * @memberof Sgke360dInspectionTreatmentComponent
   */
  constructor(
    private parent: FormGroupDirective,
  ) { }


  /**
   * 初期化処理
   *
   * @memberof Sgke360dInspectionTreatmentComponent
   */
  ngOnInit(): void {
    // 作業種別コード
    this.sagyoshubetsuCD = this.suppntInfo.workClscode;

    // 親フォームを参照
    this.parentForm = this.parent.form.get('supplyresumptionResultInfo') as UntypedFormGroup;
    this.onChangeCheck();
  }

  /**
   * 狩猟処理
   *
   * @memberof Sgke360dInspectionTreatmentComponent
   */
  ngOnDestroy(): void {
    this.parentForm.clearValidators();
    this.parentForm.updateValueAndValidity();
  }

  /**
   * 順次処理リストの取得
   *
   * @memberof Sgke360dInspectionTreatmentComponent
   */
  get checkList(): CheckList[] {
    // 一般供給再開
    const isIppan = this.sagyoshubetsuCD == '2'
    // マイコンメータフラグ
    const isMicon = this.suppntInfo.meterBunruiCode == '02';

    // 標準チェック処理関数
    const checkStandard = (ctl: FormControl, value?: any): boolean => {
      // 異常無
      return ctl.enabled && ctl.value == value;
    };
    // メーターガス栓異常チェック関数
    const checkGassenAbnormal = (ctl: FormControl, value?: any): boolean => {
      // 異常無
      let ret = checkStandard(ctl, value);
      // 異常有かつ供給停止時メーターガス栓閉止
      ret ||= checkStandard(ctl, '1') && this.shishinInputTab.supplysuspensiontimeMetergassenFlg == '0';
      return ret;
    };
    // メーター指針動止確認チェック処理関数
    const checkShishin = (ctl: FormControl, value?: any): boolean => {
      // メーター指針有効かつバリデーションOK
      return ctl.enabled && ctl.valid;
    };
    // 漏洩検査チェック処理関数
    const checkAtrk = (ctl: FormControl, value?: any): boolean => {
      // 漏えい検査有効
      return ctl.enabled;
    };
    // 漏洩時間チェック処理関数
    const checkTime = (ctl: FormControl, value?: any): boolean => {
      const leaktestAtrkCtl = this.getControl('leaktestAtrk');
      // 漏えい時間有効 かつ バリデーションOK
      return ctl.enabled && leaktestAtrkCtl.valid && ctl.valid;
    };

    // チェックリスト
    const checkList: CheckList[] = [
      // メーター外観異常
      { control: 'meterExteriorAbnormalflag', value: '0', enable: true, check: checkStandard },
      // メータガス栓異常
      { control: 'meterGassenAbnormalflag', value: '0', enable: true, check: checkGassenAbnormal },
      // マイコンメーターテスト遮断異常
      { control: 'microcontrollermeterTestblockAbnormalflag', value: '0', enable: isMicon, check: checkStandard },
      // マイコンメーター復帰異常
      { control: 'microcontrollermeterReturnAbnormalflag', value: '0', enable: isMicon, check: checkStandard },

      // 簡易供給再開
      // 動止確認漏洩検査可否
      { control: 'operationcheckLeaktestKahiflg', value: '1', enable: !isIppan, check: checkStandard },
      // メーター指針動止確認コード
      { control: 'meterShishinstartandstopConfirmationcode', value: null, enable: !isIppan, check: checkShishin },

      // 一般供給再開
      // 漏洩検査・圧力
      { control: 'leaktestAtrk', value: null, enable: isIppan, check: checkAtrk },
      // 漏洩検査・時間
      { control: 'leaktestTime', value: null, enable: isIppan, check: checkTime },

      // 他共通
      // 確認結果
      { control: 'leaktestConfirmationresultFlg', value: null, enable: true, check: null },
    ];

    // チェックリストを返す
    return checkList;
  }

  /**
   * 活性非活性リセット順次処理
   *
   * @memberof Sgke360dInspectionTreatmentComponent
   */
  onChangeCheck() {
    // 一般供給再開
    const isIppan = this.sagyoshubetsuCD == '2'
    let preCheckNg: boolean = false;
    if (isIppan) {
      // 立会人
      const taiper = this.getControl('supplyresumptionTaiperDivcd');
      // 周知不可
      const witness = this.getControl('witnessNotnoticeFlg');
      // 立会人未入力または周知不可の場合
      preCheckNg = !taiper.value || (taiper.value == '90' && witness.value == '1');
    } else {
      // マイコンメーター
      const miconMeter = this.getControl('microcontrollermeterUmuFlg');
      preCheckNg = miconMeter?.value != '1';
    }

    // 画面コントロールの順次処理
    this.checkList.reduce((prev, current) => {
      // 現在のコントロール
      const currCtl = this.getControl(current.control);
      // 1つ前のコントロール（初期値はコントロール名nullのためその際はnull）
      const prevCtl = prev.control ? this.getControl(prev.control) : null;

      // チェック関数が存在する場合チェックする。
      if (current.enable && prev?.check && prev.check(prevCtl, prev.value)) {
        //チェックOKの場合は今コントロール有効
        currCtl.enable();
      } else {
        // チェックNGの場合は今コントロール無効かつリセット
        currCtl.disable();
        currCtl.setValue(null);
      }
      // currentが有効の場合はcurrentを次へ、そうでない場合はひとつ前を引き継ぐ
      return current.enable ? current : prev;
    }, { control: null, value: null, enable: null, check: (nxt, value) => !preCheckNg });

    // 異常有/検査結果否をチェックするコントロール一覧
    const abnomalCheckCtlList = [
      { control: 'meterExteriorAbnormalflag', value: '1' },
      { control: 'microcontrollermeterTestblockAbnormalflag', value: '1' },
      { control: 'microcontrollermeterReturnAbnormalflag', value: '1' },
      { control: 'operationcheckLeaktestKahiflg', value: '0' },
      { control: 'leaktestConfirmationresultFlg', value: '0' },
      { control: 'meterShishinstartandstopConfirmationcode', value: '' },
    ];
    // 一つでも異常有を検出
    let abnomal = abnomalCheckCtlList.some((check) => {
      const ctl = this.getControl(check.control);
      // 比較値と一致する値を持つコントロール
      return ctl.enabled && (ctl?.value ?? '') == check.value;
    });
    // メータガス栓異常は供給停止時メータガス栓閉止フラグがオンの場合のみ異常状態
    abnomal ||= this.getControl('meterGassenAbnormalflag').value == '1' && this.shishinInputTab.supplysuspensiontimeMetergassenFlg != '0';

    // 中断時の処置にあるコントロール一覧
    const abnormalCtlList = [
      { control: 'meterGassenShutoffflag', value: '0' },
      { control: 'meterPauseSochiflag', value: '0' },
      { control: 'inspectiontaskTakeovertoCode', value: null },
    ];
    // 1つでも異常有がある場合中断を有効
    abnormalCtlList.forEach(ctlName => {
      const ctl = this.getControl(ctlName.control);
      if (abnomal) {
        ctl?.enable();
      } else {
        ctl?.disable();
        ctl?.setValue(ctlName.value);
      }
    });
  }

  /**
   * フォームコントロール取得
   * @param ctlname コントロール名
   * @returns フォームコントロール
   */
  getControl(ctlname: string): FormControl {
    return this.parentForm?.get(ctlname) as FormControl;
  }

}
